import React from 'react';
import SignatureCanvas from 'react-signature-canvas';
import type SignatureCanvasType from 'react-signature-canvas';
import type { IntakeFormField } from '../../api/bookingApi';
import BirthdayField from './BirthdayField';
import { birthdayPartsFromISO, isBirthdayField, parseBirthday, type BirthdayParts } from './birthday';
import { getLocalToday } from '../../utils/date';

interface CustomFormFieldProps {
  field: IntakeFormField;
  value: unknown;
  onChange: (value: unknown) => void;
  sigRef?: (el: SignatureCanvasType | null) => void;
  onClearSig?: () => void;
  /** id for the field's single native control, so an external <label htmlFor> can name it. */
  inputId?: string;
  birthdayParts?: BirthdayParts;
  onBirthdayPartChange?: (part: keyof BirthdayParts, value: string) => void;
  birthdayErrorId?: string;
}

export default function CustomFormField({
  field,
  value,
  onChange,
  sigRef,
  onClearSig,
  inputId,
  birthdayParts,
  onBirthdayPartChange,
  birthdayErrorId,
}: CustomFormFieldProps) {
  const strVal = (value as string) ?? '';

  // v2 placeholders are gray-500 (4.8:1) — gray-300 on white is 1.6:1, unreadable
  // for anyone with low vision and below WCAG AA for placeholder text.
  const baseInputClass =
    'w-full rounded-xl border-2 border-gray-100 bg-white px-4 py-3 text-base outline-none transition-all focus:border-primary/50 focus:ring-2 focus:ring-primary/20 placeholder:text-gray-500';

  // Touch-sized option rows: the whole bordered row toggles the native box.
  const optionRowClass =
    'flex items-center gap-3 min-h-[48px] p-3 rounded-xl border-2 border-gray-100 hover:border-primary/30 transition-colors cursor-pointer';

  switch (field.field_type) {
    case 'textarea':
    case 'address':
      return (
        <textarea
          id={inputId}
          className={`${baseInputClass} resize-none`}
          rows={3}
          placeholder={field.placeholder ?? ''}
          value={strVal}
          onChange={(e) => onChange(e.target.value)}
          autoComplete={field.field_type === 'address' ? 'street-address' : undefined}
        />
      );

    case 'select': {
      const select = (
        <select
          id={inputId}
          className={`${baseInputClass} appearance-none pr-10`}
          value={strVal}
          onChange={(e) => onChange(e.target.value)}
        >
          <option value="">{field.placeholder ?? 'Select...'}</option>
          {(field.options ?? []).map((opt) => (
            <option key={opt} value={opt}>{opt}</option>
          ))}
        </select>
      );
      // Native select (the e2e walk depends on it), styled with our own chevron.
      return (
        <div className="relative">
          {select}
          <svg className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      );
    }

    case 'multiselect': {
      const selected = Array.isArray(value) ? (value as string[]) : [];
      const options = field.options ?? [];
      // A long checklist (the 40-item medical-history list) reads as a dense
      // two-column grid on phones instead of forty stacked rows.
      const dense = options.length > 8;
      return (
        <div className={dense ? 'grid grid-cols-2 gap-2' : 'space-y-2'}>
          {options.map((opt) => (
            <label
              key={opt}
              className={
                dense
                  ? 'flex items-center gap-2.5 min-h-[44px] p-2.5 rounded-xl border-2 border-gray-100 hover:border-primary/30 transition-colors cursor-pointer'
                  : optionRowClass
              }
            >
              <input
                type="checkbox"
                checked={selected.includes(opt)}
                onChange={(e) => {
                  if (e.target.checked) onChange([...selected, opt]);
                  else onChange(selected.filter((s) => s !== opt));
                }}
                className="h-5 w-5 shrink-0 rounded border-gray-300 text-primary focus:ring-primary/20"
              />
              {/* min-w-0 + break-words: a long unbreakable token (e.g.
                  "Coumadin/Plavix/Eliquis") must wrap, not overflow the
                  viewport — phones zoom the whole page out otherwise. */}
              <span className={`min-w-0 break-words text-gray-700 ${dense ? 'text-sm leading-snug' : 'text-base'}`}>{opt}</span>
            </label>
          ))}
        </div>
      );
    }

    case 'checkbox': {
      // v2: a yes/no question is answered with an explicit Yes or No, not a box
      // whose empty state is ambiguous. Two native radios (visually hidden, so
      // keyboard, screen readers and the e2e walk still see real controls)
      // post the same boolean the checkbox posted. An acknowledgement that must
      // be ticked (must_be_true) keeps the checkbox row.
      if (!field.must_be_true) {
        const name = inputId ?? `yesno-${field.key}`;
        const choice = (label: string, checked: boolean, pick: () => void) => (
          <label
            className={`flex items-center justify-center min-h-[48px] rounded-xl border-2 cursor-pointer text-base font-semibold transition-colors has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-primary/40 ${
              checked ? 'border-primary-deep bg-primary/10 text-primary-deep' : 'border-gray-100 text-gray-700 hover:border-primary/30'
            }`}
          >
            <input type="radio" name={name} value={label.toLowerCase()} className="sr-only" checked={checked} onChange={pick} />
            {label}
          </label>
        );
        return (
          <fieldset className="min-w-0">
            {/* A legend is shrink-to-fit and ignores the fieldset width unless sized
                explicitly — without w-full a long unbreakable token overflows the
                viewport and phones zoom out. */}
            <legend className="block w-full max-w-full text-sm font-semibold text-gray-700 mb-2 break-words">
              {field.label}
              {field.required && <span className="text-primary-deep ml-1">*</span>}
            </legend>
            <div className="grid grid-cols-2 gap-2">
              {choice('Yes', value === true, () => onChange(true))}
              {choice('No', value === false, () => onChange(false))}
            </div>
          </fieldset>
        );
      }
      return (
        <label className={optionRowClass}>
          <input
            type="checkbox"
            checked={!!value}
            onChange={(e) => onChange(e.target.checked)}
            className="h-5 w-5 shrink-0 rounded border-gray-300 text-primary focus:ring-primary/20"
          />
          <span className="min-w-0 break-words text-base text-gray-700">{field.label}</span>
        </label>
      );
    }

    case 'signature':
    case 'initials':
      return (
        <div className="space-y-3">
          <div className="border-2 border-gray-100 rounded-2xl overflow-hidden bg-white hover:border-primary/30 transition-colors relative">
            <SignatureCanvas
              ref={(el) => sigRef?.(el)}
              penColor="black"
              clearOnResize={false}
              canvasProps={{
                className: field.field_type === 'initials'
                  ? 'w-full h-28'
                  : 'w-full h-40 sm:h-48',
                style: { touchAction: 'none' },
              }}
            />
            <div aria-hidden className="pointer-events-none absolute inset-x-5 bottom-5 border-b border-dashed border-gray-300" />
          </div>
          <button
            type="button"
            className="min-h-[44px] px-4 rounded-xl border border-gray-200 bg-white text-sm font-semibold text-gray-500 hover:text-gray-700 hover:border-gray-300 transition-colors"
            onClick={onClearSig}
          >
            Clear
          </button>
        </div>
      );

    case 'file_upload':
      // Unreachable from v2: V2SectionStep mounts FileUploadField itself for every
      // staging key and validateV2Package fails closed on any other `file_upload` key.
      // The V1 renderers that reached this branch were retired (backend#1604 slice 2);
      // it stays only so the switch remains exhaustive.
      return (
        <p className="text-sm text-gray-500 italic">File upload is unavailable here.</p>
      );

    case 'date':
      if (isBirthdayField(field)) {
        const parts = birthdayParts ?? birthdayPartsFromISO(value);
        return (
          <BirthdayField
            field={field}
            parts={parts}
            inputId={inputId}
            errorId={birthdayErrorId}
            className={baseInputClass}
            onPartChange={onBirthdayPartChange ?? ((part, partValue) => {
              const result = parseBirthday({ ...parts, [part]: partValue }, getLocalToday());
              onChange(result.status === 'valid' ? result.iso : '');
            })}
          />
        );
      }
      return (
        <input
          id={inputId}
          type="date"
          className={baseInputClass}
          value={strVal}
          onChange={(event) => onChange(event.target.value)}
          autoComplete={field.key === 'date_of_birth' ? 'bday' : undefined}
        />
      );

    default: {
      const typeMap: Record<string, string> = {
        text: 'text', email: 'email', phone: 'tel', number: 'number',
      };
      // v2: numbers are text + decimal keypad — type="number" brings spinner
      // arrows and leading-zero trouble; the payload sends typed strings anyway.
      const inputType = field.field_type === 'number' ? 'text' : (typeMap[field.field_type] ?? 'text');
      const modeOverride: React.InputHTMLAttributes<HTMLInputElement> =
        field.field_type === 'number' ? { inputMode: 'decimal' } : {};
      return (
        <input
          id={inputId}
          type={inputType}
          className={baseInputClass}
          placeholder={field.placeholder ?? ''}
          value={strVal}
          onChange={(e) => onChange(e.target.value)}
          {...mobileInputAttributes(field, inputType)}
          {...modeOverride}
        />
      );
    }
  }
}

/**
 * Keyboard/autofill hints for the mobile browser — attributes only, never the
 * field's value or change handling.
 */
function mobileInputAttributes(
  field: IntakeFormField,
  inputType: string,
): React.InputHTMLAttributes<HTMLInputElement> {
  const byType: Record<string, React.InputHTMLAttributes<HTMLInputElement>> = {
    email: { inputMode: 'email', autoComplete: 'email', autoCapitalize: 'none' },
    tel: { inputMode: 'tel', autoComplete: 'tel' },
    number: { inputMode: 'numeric' },
    text: { enterKeyHint: 'next' },
  };
  const byKey: Record<string, React.InputHTMLAttributes<HTMLInputElement>> = {
    first_name: { autoComplete: 'given-name', autoCapitalize: 'words' },
    last_name: { autoComplete: 'family-name', autoCapitalize: 'words' },
  };
  const key = field.key || '';
  const heuristics: React.InputHTMLAttributes<HTMLInputElement> =
    /zip|postal/i.test(key) ? { autoComplete: 'postal-code', inputMode: 'numeric' }
    : /city/i.test(key) ? { autoComplete: 'address-level2', autoCapitalize: 'words' }
    : /^state$|_state/i.test(key) ? { autoComplete: 'address-level1', autoCapitalize: 'characters' }
    : /name/i.test(key) ? { autoCapitalize: 'words' }
    : {};
  return { ...byType[inputType], ...heuristics, ...byKey[key] };
}
